import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Cell } from 'recharts';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';

const CATEGORY_COLORS: Record<string, string> = {
    campaign: CHART_COLORS.chart.line1,
    ad_set: CHART_COLORS.chart.line5,
    ad_group: CHART_COLORS.chart.line5,
    ad: CHART_COLORS.chart.line4,
    product: CHART_COLORS.chart.line2,
    order: CHART_COLORS.chart.line6,
    account: CHART_COLORS.gray[500],
    price_rule: CHART_COLORS.chart.line3,
    collection: CHART_COLORS.accent,
};

export const ActionBreakdownByTypeChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { events, isLoading } = useBoardChartData();

    const chartData = useMemo(() => {
        if (!events) return [];
        const counts = new Map<string, number>();
        events.forEach((e: any) => {
            const cat = e.category || 'other';
            counts.set(cat, (counts.get(cat) || 0) + 1);
        });
        return Array.from(counts.entries())
            .map(([category, count]) => ({
                name: category.replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
                count,
                color: CATEGORY_COLORS[category] || CHART_COLORS.gray[400],
            }))
            .sort((a, b) => b.count - a.count);
    }, [events]);

    const isEmpty = !isLoading && chartData.length === 0;
    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData} layout="vertical" margin={{ ...CHART_STYLES.margin.withAxis, left: 80 }}>
                        <CartesianGrid strokeDasharray={CHART_STYLES.grid.strokeDasharray} horizontal={false} stroke={CHART_STYLES.grid.stroke} opacity={CHART_STYLES.grid.opacity} />
                        <XAxis type="number" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} />
                        <YAxis dataKey="name" type="category" {...CHART_STYLES.axis} tick={{ ...CHART_STYLES.axis.tick, fontSize: 10 }} width={80} />
                        <Tooltip contentStyle={CHART_STYLES.tooltip.contentStyle} />
                        <Bar dataKey="count" name={t('charts.count', 'Count') as string} radius={[0, 3, 3, 0]} maxBarSize={24}>
                            {chartData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                        </Bar>
                    </BarChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
